"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { LuSend, LuX } from "react-icons/lu";
import axios from "axios";
import { useAuthStore } from "@/store/useAuthStore";
import { useChatStore } from "@/store/useChatStore";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

export interface ComposePopoverProps {
  expertUserId: string;
  expertName: string;
  anchorRect: DOMRect;
  onClose: () => void;
}

export function ComposePopover({
  expertUserId,
  expertName,
  anchorRect,
  onClose,
}: ComposePopoverProps) {
  const { accessToken } = useAuthStore();
  const openPanel = useChatStore((s) => s.openPanel);
  const setPendingOpenConvId = useChatStore((s) => s.setPendingOpenConvId);

  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const POPOVER_W = 288;
  const GAP = 8;
  const vpW = typeof window !== "undefined" ? window.innerWidth : 1200;
  const rawLeft = anchorRect.left;
  const left = Math.max(8, Math.min(rawLeft, vpW - POPOVER_W - 8));
  const top = anchorRect.bottom + GAP;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const id = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handler);
    };
  }, [onClose]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSend = useCallback(async () => {
    const body = text.trim();
    if (!body || loading || !accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const convRes = await axios.post(
        `${API}/direct-chat/conversations`,
        { expertUserId },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      await axios.post(
        `${API}/direct-chat/conversations/${convRes.data.id}/messages`,
        { body },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      setPendingOpenConvId(convRes.data.id);
      openPanel();
      onClose();
    } catch {
      setError("Failed to send. Please try again.");
      setLoading(false);
    }
  }, [text, loading, accessToken, expertUserId, setPendingOpenConvId, openPanel, onClose]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === "Escape") onClose();
  };

  return (
    <div
      ref={popoverRef}
      style={{ position: "fixed", top, left, width: POPOVER_W, zIndex: 300 }}
      className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-white/10 shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-indigo-600 dark:bg-indigo-700">
        <div className="min-w-0">
          <p className="text-[10px] text-indigo-200 font-medium uppercase tracking-wide leading-none mb-0.5">
            Message
          </p>
          <p className="text-sm font-semibold text-white truncate">{expertName}</p>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 p-1 rounded-lg text-indigo-200 hover:text-white hover:bg-indigo-500 transition-colors"
          aria-label="Close"
        >
          <LuX className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-2">
        <textarea
          ref={textareaRef}
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKey}
          placeholder={`Write to ${expertName}… (Enter to send)`}
          className="w-full resize-none text-sm bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-3 py-2.5 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 leading-relaxed"
        />

        {error && (
          <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            Shift+Enter for new line
          </p>
          <button
            onClick={handleSend}
            disabled={!text.trim() || loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold transition-colors"
          >
            {loading ? (
              <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <LuSend className="h-3 w-3" />
            )}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
